import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { UserType } from '../../types';
import { DataChannelMessageType, DataChannelSessionIntentionSetMessage, useSendMessage } from '../../video-sdk';
import { participantUserIdsAtom } from '../../video-sdk/core/room/participant/atoms';
import { inSessionPatientInformationAtom, providerShortIdAtom } from '../state';

export function useSendSessionIntentionSetCallback() {
  const getProviderShortId = useAtomCallback(useCallback((get) => get(providerShortIdAtom), []));
  const getParticipantUserIds = useAtomCallback(useCallback((get) => get(participantUserIdsAtom), []));
  const getInSessionPatientInfo = useAtomCallback(useCallback((get) => get(inSessionPatientInformationAtom), []));
  const sendMessage = useSendMessage();
  const setInSessionPatientInformation = useSetAtom(inSessionPatientInformationAtom);

  return useCallback(
    async (shortId: string) => {
      const completedAt = new Date().toISOString();
      const message: DataChannelSessionIntentionSetMessage = {
        type: DataChannelMessageType.SESSION_INTENTION_SET,
        sender: {
          entityId: getProviderShortId() ?? '',
          entityType: UserType.PROVIDER,
        },
        sessionIntention: {
          shortId,
          completedAt,
        },
      };

      const didSendMessage = await sendMessage(message);

      if (didSendMessage) {
        const participantUserIds = getParticipantUserIds();
        const inSessionPatient = getInSessionPatientInfo();
        if (inSessionPatient && participantUserIds.includes(inSessionPatient.visitorUuid || '')) {
          setInSessionPatientInformation({
            ...inSessionPatient,
            intentionCompletedAt: completedAt,
          });
        }
      }

      return didSendMessage;
    },
    [sendMessage, getProviderShortId, getParticipantUserIds, getInSessionPatientInfo, setInSessionPatientInformation],
  );
}
